import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'gatsby';
import Logo from './Logo';
import { useTheme } from '../contexts/ThemeContext';

const Nav = styled.nav`
  background: var(--nav-bg);
  border-bottom: 1px solid var(--nav-border);
  position: sticky;
  top: 0;
  z-index: 50;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  transition: background 0.2s, border-color 0.2s;
`;

const NavInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 4rem;
  @media (min-width: 640px) { padding: 0 1.5rem; }
  @media (min-width: 1024px) { padding: 0 2rem; }
`;

const LogoArea = styled.div`
  flex-shrink: 0;
  a { text-decoration: none; }
`;

const Tagline = styled.span`
  display: block;
  font-size: 0.7rem;
  color: var(--text-muted);
  margin-top: 2px;
  font-style: italic;
  font-weight: 500;
`;

const NavLink = styled(Link)`
  font-weight: 500;
  font-size: 0.875rem;
  text-decoration: none;
  color: var(--text-secondary);
  border-bottom: 2px solid transparent;
  padding-bottom: 4px;
  transition: color 0.2s ease, border-color 0.2s ease;
  white-space: nowrap;
  &:hover {
    color: var(--link);
    border-bottom-color: var(--link);
  }
  &.active {
    color: var(--link);
    border-bottom-color: var(--link);
  }
`;

const DesktopLinks = styled.div`
  display: none;
  align-items: center;
  gap: 1.5rem;
  @media (min-width: 768px) { display: flex; }
  @media (min-width: 1024px) { gap: 2rem; }
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ThemeToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: 0.8rem;
  font-weight: 500;
  padding: 4px 6px;
  border-radius: 6px;
  transition: color 0.2s, background 0.2s;
  &:hover {
    color: var(--link);
    background: var(--bg-tertiary);
  }
  span { display: none; @media (min-width: 1024px) { display: inline; } }
`;

const HamburgerBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;
  transition: color 0.2s;
  &:hover { color: var(--link); }
  @media (min-width: 768px) { display: none; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 40;
`;

const Drawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: 16rem;
  background: var(--nav-bg);
  z-index: 50;
  box-shadow: -4px 0 20px rgba(0,0,0,0.25);
  display: flex;
  flex-direction: column;
  transition: background 0.2s;
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
  border-bottom: 1px solid var(--border);
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 4px;
  transition: color 0.2s;
  display: flex;
  align-items: center;
  &:hover { color: var(--link); }
`;

const DrawerLinks = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0;
`;

const DrawerLink = styled(Link)`
  display: block;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  font-size: 0.9375rem;
  text-decoration: none;
  color: var(--text-secondary);
  transition: background 0.15s;
  &:hover { background: var(--hover-bg); }
  &.active {
    background: var(--bg-tertiary);
    color: var(--link);
    border-left: 4px solid var(--link);
    padding-left: calc(1.5rem - 4px);
  }
`;

const NAV_LINKS = [
  { name: 'Home', href: '/' },
  { name: 'Politics', href: '/category/politics' },
  { name: 'Entertainment', href: '/category/entertainment' },
  { name: 'Health', href: '/category/health' },
  { name: 'NRI', href: '/category/nri' },
  { name: 'Videos', href: '/category/videos' },
  { name: 'Gallery', href: '/gallery' },
  { name: 'Editorial', href: '/category/editorial' },
];

const SunIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const MoonIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
  </svg>
);

const NavBar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <Nav>
      <NavInner>
        <LogoArea>
          <Link to="/">
            <Logo dark={!isDark} />
            <Tagline>Real News. Real People.</Tagline>
          </Link>
        </LogoArea>

        <DesktopLinks>
          {NAV_LINKS.map(link => (
            <NavLink
              key={link.name}
              to={link.href}
              activeClassName="active"
              partiallyActive={link.href !== '/'}
            >
              {link.name}
            </NavLink>
          ))}
        </DesktopLinks>

        <RightControls>
          <ThemeToggle onClick={toggleTheme} aria-label="Toggle theme">
            {isDark ? <SunIcon /> : <MoonIcon />}
            <span>{isDark ? 'Light' : 'Dark'}</span>
          </ThemeToggle>

          <HamburgerBtn onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </HamburgerBtn>
        </RightControls>
      </NavInner>

      {mobileMenuOpen && (
        <>
          <Overlay onClick={() => setMobileMenuOpen(false)} />
          <Drawer>
            <DrawerHeader>
              <CloseBtn onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </CloseBtn>
            </DrawerHeader>
            <DrawerLinks>
              {NAV_LINKS.map(link => (
                <DrawerLink
                  key={link.name}
                  to={link.href}
                  activeClassName="active"
                  partiallyActive={link.href !== '/'}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </DrawerLink>
              ))}
            </DrawerLinks>
          </Drawer>
        </>
      )}
    </Nav>
  );
};

export default NavBar;
